import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button } from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import { BRAND } from "src/theme/brandTokens";
import { LOGO_PRINCIPAL_BLANCO_SRC } from "src/utils/logoSources.js";
import {
  formatFechaPortada,
  obtenerAnio,
  splitTituloBicolor,
} from "src/utils/propuestaCoverFormat.js";

const ProposalHeaderV2 = ({ propuesta, onDownloadPDF }) => {
  const titulo = propuesta.titulo || "Propuesta de Servicios";
  const { linea1, linea2 } = splitTituloBicolor(titulo);
  const fechaRef = propuesta.fechaEnvioCorreo;
  const anio = obtenerAnio(fechaRef);
  const fechaCorta = formatFechaPortada(fechaRef);
  const mostrarBotonPDF = !!(propuesta.urlPropuesta && onDownloadPDF);

  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        bgcolor: BRAND.color.inkSoft,
        color: BRAND.color.paper,
        fontFamily: BRAND.font.family.base,
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        minHeight: { xs: "auto", md: 300 },
      }}
    >
      {/* Bloque izquierdo: logo + eyebrow + título mega bicolor */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: { xs: 4, md: 6 },
          px: { xs: 3, sm: 5, md: 8 },
          py: { xs: 4, md: 6 },
          minWidth: 0,
        }}
      >
        <Box
          component="img"
          src={LOGO_PRINCIPAL_BLANCO_SRC}
          alt="TAXTIC"
          sx={{ height: 32, width: 115, display: "block" }}
        />
        <Box>
          <Typography
            component="div"
            sx={{
              fontFamily: BRAND.font.family.base,
              fontSize: BRAND.font.size.eyebrow,
              color: BRAND.color.grey50,
              textTransform: "uppercase",
              letterSpacing: BRAND.font.letterSpacing.widest,
              mb: 1.5,
            }}
          >
            Propuesta · {anio}
          </Typography>
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontFamily: BRAND.font.family.display,
              fontWeight: BRAND.font.weight.black,
              fontSize: { xs: "30pt", sm: "38pt", md: BRAND.font.size.hero },
              lineHeight: BRAND.font.lineHeight.tight,
              letterSpacing: BRAND.font.letterSpacing.tight,
              wordBreak: "break-word",
            }}
          >
            <Box
              component="span"
              sx={{ display: "block", color: BRAND.color.paper }}
            >
              {linea1}
            </Box>
            {linea2 && (
              <Box
                component="span"
                sx={{ display: "block", color: BRAND.color.primary }}
              >
                {linea2}
              </Box>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Bloque derecho: cuadrado naranja con botón PDF arriba e info abajo.
          En desktop ocupa todo el alto (180px ancho). En mobile colapsa a una
          franja superior con el contenido apilado encima del bloque izquierdo. */}
      <Box
        sx={{
          position: "relative",
          width: { xs: "100%", md: 180 },
          minHeight: { xs: 0, md: "auto" },
          bgcolor: BRAND.color.primary,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "flex-end" },
          gap: 2,
          px: { xs: 3, sm: 5, md: 2.5 },
          py: { xs: 2.5, md: 4 },
          order: { xs: -1, md: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          {mostrarBotonPDF && (
            <Button
              onClick={onDownloadPDF}
              startIcon={<GetApp />}
              variant="outlined"
              size="small"
              sx={{
                color: BRAND.color.paper,
                borderColor: "rgba(255,255,255,0.7)",
                fontFamily: BRAND.font.family.base,
                fontSize: BRAND.font.size.eyebrow,
                fontWeight: BRAND.font.weight.bold,
                textTransform: "uppercase",
                letterSpacing: BRAND.font.letterSpacing.wide,
                borderRadius: `${BRAND.radius.xs}px`,
                px: 1.5,
                "&:hover": {
                  borderColor: BRAND.color.paper,
                  bgcolor: "rgba(255,255,255,0.12)",
                },
              }}
            >
              Descargar PDF
            </Button>
          )}
        </Box>
        <Box
          sx={{
            color: BRAND.color.paper,
            fontFamily: BRAND.font.family.base,
            fontSize: BRAND.font.size.eyebrow,
            textTransform: "uppercase",
            letterSpacing: BRAND.font.letterSpacing.wide,
            lineHeight: BRAND.font.lineHeight.snug,
            textAlign: { xs: "right", md: "right" },
          }}
        >
          <Box
            component="div"
            sx={{ fontWeight: BRAND.font.weight.bold, mb: 0.25 }}
          >
            #{propuesta.id}
          </Box>
          <Box component="div">{fechaCorta}</Box>
        </Box>
      </Box>
    </Box>
  );
};

ProposalHeaderV2.propTypes = {
  propuesta: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    titulo: PropTypes.string,
    fechaEnvioCorreo: PropTypes.string,
    urlPropuesta: PropTypes.string,
  }).isRequired,
  onDownloadPDF: PropTypes.func,
};

export default React.memo(ProposalHeaderV2);
